import {WishlistCollection} from '../../@types/wishlist.js';

export enum CollectionApiError {
  INVALID = 'collection-error-name-invalid',
  BLANK = 'collection-error-name-blank',
  TOO_LONG = 'collection-error-name-too-long',
  ALREADY_EXISTS = 'collection-error-name-already-exists',
  UNEXPECTED = 'collection-error-unexpected',
}

class AbstractCollectionApiResponse<Type> {
  readonly isOk: boolean;
  readonly errorCodes?: CollectionApiError[];
  protected readonly result?: Type;

  protected constructor(
    isOk: boolean,
    errorCodes?: CollectionApiError[],
    result?: Type
  ) {
    this.isOk = isOk;
    this.errorCodes = errorCodes;
    this.result = result;
  }

  toCollectionApiResponse(): CollectionApiResponse {
    return new CollectionApiResponse(this.isOk, this.errorCodes, this.result as WishlistCollection);
  }

  toCollectionDeletionApiResponse(): CollectionDeletionApiResponse {
    return new CollectionDeletionApiResponse(
      this.isOk,
      this.errorCodes,
      (this.result as { collectionId: string })?.collectionId
    );
  }

  static ok<T>(result: T): AbstractCollectionApiResponse<T> {
    return new AbstractCollectionApiResponse<T>(true, undefined, result);
  }

  static error(errorCodes: CollectionApiError[]): AbstractCollectionApiResponse<undefined> {
    return new AbstractCollectionApiResponse(false, errorCodes, undefined);
  }
}

class CollectionApiResponse extends AbstractCollectionApiResponse<WishlistCollection> {
  readonly collection?: WishlistCollection = this.result;
}

class CollectionDeletionApiResponse extends AbstractCollectionApiResponse<string> {
  readonly collectionId?: string = this.result;
}

export class CollectionApiInstance {
  async add(endpoint: string, collectionName: string, created?: string): Promise<CollectionApiResponse> {
    return this.send(endpoint, 'POST', {
      collectionName,
      created
    }).then(result => result.toCollectionApiResponse());
  }

  async update(endpoint: string, updatedCollection: WishlistCollection): Promise<CollectionApiResponse> {
    return this.send(endpoint, 'PUT', updatedCollection).then(result => result.toCollectionApiResponse());
  }

  async delete(endpoint: string, collectionId: string, removeItemsFromFavorites: boolean): Promise<CollectionDeletionApiResponse> {
    return this.send(endpoint, 'DELETE', {
      collectionId,
      removeItemsFromFavorites: removeItemsFromFavorites
    }).then(result => result.toCollectionDeletionApiResponse());
  }

  async addItem(
    endpoint: string,
    collectionId: string,
    syan: number,
    colorId?: string
  ): Promise<CollectionApiResponse> {
    return this.send(`${endpoint}${collectionId}/p/`, 'POST', {
      syan,
      colorId,
    }).then(result => result.toCollectionApiResponse());
  }

  async deleteItem(
    endpoint: string,
    collectionId: string,
    syan: number,
    colorId?: string
  ): Promise<CollectionApiResponse> {
    return this.send(`${endpoint}${collectionId}/p/`, 'DELETE', {
      syan,
      colorId,
    }).then(result => result.toCollectionApiResponse());
  }

  private async send(
    endpoint: string,
    method: string,
    bodyAsJson: object
  ) {
    try {
      const response = await fetch(endpoint, {
        method: method,
        body: JSON.stringify(bodyAsJson),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      if (response.ok) {
        return AbstractCollectionApiResponse.ok(result);
      } else {
        return AbstractCollectionApiResponse.error(result.errorCodes || []);
      }
    } catch (error) {
      console.error('Error creating collection:', error);
      return AbstractCollectionApiResponse.error([CollectionApiError.UNEXPECTED]);
    }
  }
}

export const collectionApi = new CollectionApiInstance();
